import React, { useRef, useEffect, useCallback, useRef as useRefAlias } from "react";
import DOMPurify from "dompurify";

const ALLOWED_TAGS = ["p","br","strong","em","u","s","a","ul","ol","li","blockquote","code"];
const ALLOWED_ATTR = ["href","target","rel"];

function normalizeHTML(html, mode = "edit") {
  const wrap = document.createElement("div");
  wrap.innerHTML = html;

  wrap.querySelectorAll("div").forEach(div => {
    const p = document.createElement("p");
    while (div.firstChild) p.appendChild(div.firstChild);
    div.replaceWith(p);
  });

  wrap.innerHTML = wrap.innerHTML.replace(/(?:<br>\s*){3,}/g, "<br><br>");

  if (mode === "save") {
    while (wrap.lastChild && (
      wrap.lastChild.nodeName === "BR" ||
      (wrap.lastChild.nodeName === "P" && !wrap.lastChild.textContent.trim()) ||
      (wrap.lastChild.nodeType === 3 && !wrap.lastChild.nodeValue.trim())
    )) {
      wrap.removeChild(wrap.lastChild);
    }
  }

  return wrap.innerHTML;
}

export default function InlineHtmlEditor({
  valueHtml,
  onChangeHtml,
  className,
  autofocus = true,
  enterAsBr = false, 
}) {
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!ref.current) return;
    const clean = DOMPurify.sanitize(valueHtml || "", {
      ALLOWED_TAGS, ALLOWED_ATTR, FORBID_TAGS: ["script","style","button"]
    });
    const normalized = normalizeHTML(clean, "edit");
    if (ref.current.innerHTML !== normalized) ref.current.innerHTML = normalized;
  }, [valueHtml]);

  const emitChange = React.useCallback(() => {
    if (!ref.current) return;
    const raw = ref.current.innerHTML;
    const normalized = normalizeHTML(raw, "edit"); 
    const sanitized = DOMPurify.sanitize(normalized, {
      ALLOWED_TAGS, ALLOWED_ATTR, FORBID_TAGS: ["script","style","button"]
    });
    if (ref.current.innerHTML !== sanitized) ref.current.innerHTML = sanitized;
    onChangeHtml?.(sanitized);
  }, [onChangeHtml]);

  const handleInput = React.useCallback(emitChange, [emitChange]);

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      ref.current?.blur();
      return;
    }
    if (enterAsBr && e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      document.execCommand("insertLineBreak"); 
      emitChange();
    }
  };

  React.useEffect(() => {
    if (autofocus && ref.current) {
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(ref.current);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
      ref.current.focus();
    }
  }, [autofocus]);

  return (
    <div
      ref={ref}
      className={className || "inline-html-editor"}
      contentEditable
      suppressContentEditableWarning
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      role="textbox"
      aria-multiline="true"
    />
  );
}
